import queries from '@apis/queries';
import useTestUser from '@hooks/test/useTestUser';
import useDebouncedValue from '@hooks/useDebouncedValue';
import useResetPage from '@hooks/useResetPage';
import { GetShopsParams, ShopInMapRow } from '@models/shop';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';

import { isMyShopAtom, queryAtom, userLatLonAtom } from '../../../atoms/shopsInMapAtoms';

interface Props {
  locationAgreed: boolean,
  originalItemId?: string,
  isOrder?: boolean
  ready: boolean,
}

function useShops({
  locationAgreed, originalItemId, isOrder, ready,
}: Props) {
  const [page, setPage] = useState<number>(0);
  const query = useAtomValue(queryAtom);
  const debouncedQuery = useDebouncedValue(query);
  const myShop = useAtomValue(isMyShopAtom);
  const [shops, setShops] = useState<ShopInMapRow[]>([]);
  const { lat, lon } = useAtomValue(userLatLonAtom);
  const params: GetShopsParams = {
    page,
    size: 10,
    lon,
    lat,
    query: debouncedQuery,
    myShop,
    originalItemId,
    locationAgreed,
    searchType: isOrder ? 'ORDER' : 'SEARCH_SHOP',
  };
  const { data, status } = useQuery({
    ...queries.shop.list(params),
    enabled: ((!debouncedQuery) || debouncedQuery.length >= 2)
        && (ready || import.meta.env.MODE === 'development'),
  });
  const isTestUser = useTestUser();
  const fetchEnabled = !data || !data.slice.last;
  useEffect(() => {
    if (data) {
      if (data.slice.first) {
        setShops(data.content);
      } else {
        setShops((prev) => [...prev, ...data.content]);
      }
    }
  }, [data]);
  const fetchNext = () => {
    if (fetchEnabled && status !== 'pending' && !data?.slice.last) {
      setPage((prev) => prev + 1);
    }
  };
  const isLoading = status === 'pending';
  const isBlank = status !== 'pending' && (!shops || shops.length === 0);

  useResetPage({ params, setPage });
  return {
    shops: shops.filter((shop) => isTestUser || !shop.isTestShop),
    fetchEnabled,
    fetchNext,
    isLoading,
    isBlank,
  };
}

export default useShops;
